import isEmpty from "lodash/isEmpty";
import { AnimatePresence, motion } from "motion/react";
import { useEffect, useState } from "react";
import { MdDelete } from "react-icons/md";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useLocation, useNavigate } from "react-router";

import Swal from "sweetalert2";
import { ICONS } from "../../assets/icons";
import FullScreenLoader from "../../components/fullScreenLoader";
import Header from "../../components/header";
import SearchInput from "../../components/searchInput";
import { fetcher } from "../../network";
import { API } from "../../network/api";
import { fetchDoctors } from "../../network/globalFetches";
import { Doctor, Guide } from "../../types";
import {
  AddDoctorsResponse,
  RemoveDoctorsResponse,
} from "../../types/responses";
import Drawer from "../home/components/drawer";
import toast, { Toaster } from "react-hot-toast";

const removeDoctorMotation = async (id: string) => {
  const { data } = await fetcher().delete<RemoveDoctorsResponse>(
    API.DELETE.REMOVE_DOCTOR(id)
  );
  return data;
};

const addDoctor = async ({
  name,
  isConfirmed,
  isDenied,
}: {
  name: string;
  isConfirmed: boolean;
  isDenied: boolean;
}) => {
  const { data } = await fetcher().post<AddDoctorsResponse>(
    API.POST.ADD_DOCTOR,
    {
      name,
    }
  );
  return { ...data, isConfirmed, isDenied };
};

const renderContent = (
  doctors: Doctor[],
  onDelete: (id: string) => void,
  isError: boolean
) => {
  if (isError) {
    return (
      <div className="flex-1 flex flex-col w-4/5 mx-auto scrollbar-hide mt-10 items-center">
        <div className="flex flex-col text-center gap-8 h-1/2 w-1/2">
          <h1 className="text-2xl">
            אופס... השרת יצא להפסקת קפה. תן לו כמה דקות ותנסה שוב! ☕
          </h1>
          <img src={ICONS.ERROR_NOT_FOUND} />
        </div>
      </div>
    );
  }
  if (isEmpty(doctors)) {
    return (
      <div className="flex flex-col text-center items-center gap-10">
        <h2 className="font-bold text-xl">לא נמצאו רופאים</h2>
        <div className="w-1/4">
          <img
            className="w-full"
            src={ICONS.DOCTORS_NOT_FOUND}
            alt="לא נמצאו רופאים"
          />
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto flex flex-col w-4/5 mx-auto py-2">
      <div className=" text-center">
        <AnimatePresence>
          {doctors.map(({ name, _id, defaultImg }, index) => {
            return (
              <motion.div
                key={_id}
                initial={{ opacity: 0, y: 0 }}
                animate={{ opacity: 1, y: 20 }}
                exit={{ opacity: 0, y: 20 }}
                transition={{ duration: 0.3 }}
                layout
                className={`flex justify-between hover:bg-blue-500 hover:cursor-pointer items-center mb-2 p-2 odd:bg-blue-300 border rounded ${
                  index === doctors.length - 1 ? "mb-14" : ""
                }`}>
                <div className="flex gap-4 text-right items-center">
                  <div className="rounded-full w-14">
                    <img
                      className="w-full rounded-full"
                      src={defaultImg}
                      alt=""
                    />
                  </div>
                  <p>{name}</p>
                </div>
                <div className="flex items-center gap-14">
                  <button
                    onClick={() => {
                      Swal.fire({
                        icon: "warning",
                        title: `${name} ימחק לתמיד`,
                        showConfirmButton: true,
                        confirmButtonText: "מחק",
                        confirmButtonColor: "red",
                        showCancelButton: true,
                        cancelButtonText: "ביטול",
                      }).then(({ isConfirmed }) => {
                        if (isConfirmed) {
                          onDelete(_id);
                        }
                      });
                    }}>
                    <MdDelete size={22} className="hover:scale-125" />
                  </button>
                </div>
              </motion.div>
            );
          })}
        </AnimatePresence>
      </div>
    </div>
  );
};

const DoctorsPage = () => {
  const [filterDoctors, setFilterDoctors] = useState<Guide[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  const queryClient = useQueryClient();

  const {
    data: doctors,
    isError,
    isLoading,
  } = useQuery({
    queryKey: fetchDoctors.queryKey,
    queryFn: fetchDoctors.fetch,
    staleTime: Infinity,
  });

  const { mutate } = useMutation<
    AddDoctorsResponse & { isConfirmed: boolean; isDenied: boolean },
    Error,
    { name: string; isConfirmed: boolean; isDenied: boolean }
  >({
    mutationFn: addDoctor,
    onSuccess: ({ doctor, isConfirmed, isDenied }) => {
      queryClient.invalidateQueries({
        queryKey: fetchDoctors.queryKey,
      });

      let message = `${doctor.name} נוסף בהצלחה`;
      if (location.state?.from) {
        message = `${doctor.name} נוסף בהצלחה :) חוזרים להוספת דייר`;
      }

      setFilterDoctors((prev) => [doctor, ...prev]);

      toast.success(message);

      if (isConfirmed) {
        if (location.state?.from) {
          setTimeout(() => {
            navigate(location.state?.from);
          }, 1000);
        }
      } else if (isDenied) {
        setTimeout(() => {
          openAddDoctorPopup();
        }, 500);
      }
    },
    onError: () => {
      toast.error("תקלת מערכת - הרופא לא נשמר לצערנו. נסה שנית מאוחר יותר");
    },
  });

  const { mutate: removeDoctor } = useMutation<
    RemoveDoctorsResponse,
    Error,
    string
  >({
    mutationFn: removeDoctorMotation,
    onSuccess: ({ doctorId }) => {
      queryClient.setQueryData<Doctor[]>(fetchDoctors.queryKey, (oldData) => {
        if (!oldData) return [];
        return oldData.filter((doctor) => doctor._id !== doctorId);
      });
      setFilterDoctors((prev) =>
        prev.filter((doctor) => doctor._id !== doctorId)
      );
      toast.success("הרופא נמחק בהצלחה");
    },
    onError: () => {
      toast.error("תקלת מערכת - הרופא לא נמחק");
    },
  });

  useEffect(() => {
    if (!doctors) {
      return;
    }

    let filtered = doctors;

    if (searchTerm) {
      filtered = filtered.filter((doctor) =>
        doctor.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilterDoctors(filtered);
  }, [searchTerm, doctors]);

  const openAddDoctorPopup = () => {
    Swal.fire<string>({
      title: "הזן שם רופא",
      input: "text",
      inputLabel: "שם הרופא",
      confirmButtonText: "שמור וסיים",
      confirmButtonColor: "#C64192",
      showDenyButton: true,
      denyButtonText: "הוסף רופא נוסף",
      denyButtonColor: "#144082",
      returnInputValueOnDeny: true,
      inputValidator: (value) => {
        if (!value) {
          return "שדה חובה";
        }
      },
    }).then(async ({ isConfirmed, isDenied, value }) => {
      if (!value) {
        return;
      }
      mutate({ name: value, isConfirmed, isDenied });
    });
  };

  const toggleDrawer = () => {
    setIsDrawerOpen((prev) => !prev);
  };

  return (
    <div className="container mx-auto h-screen flex flex-col">
      <Header>
        <Header.Time />
        <Header.Title title="ניהול רופאים" />
        <Header.Menu toggleDrawer={toggleDrawer} />
      </Header>

      {!isError && (
        <div className="w-1/3 mx-auto mt-6">
          <SearchInput placeholder="חפש רופא" onSearch={setSearchTerm} />
        </div>
      )}

      <div className="mt-10">
        {!isEmpty(filterDoctors) && !isError && (
          <p className="mb-2 w-4/5 mx-auto">
            מספר רופאים: {filterDoctors.length}
          </p>
        )}
        {renderContent(filterDoctors, removeDoctor, isError)}
      </div>
      <div className="absolute bottom-6 right-6 hover:scale-110 shadow">
        <button
          onClick={openAddDoctorPopup}
          className="bg-[#144082] text-white rounded py-3 px-8 ">
          הוסף רופא
        </button>
      </div>
      {isDrawerOpen && <Drawer toggleDrawer={toggleDrawer} />}
      <Toaster position="bottom-center" />
      {isLoading && <FullScreenLoader />}
    </div>
  );
};

export default DoctorsPage;
